import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone } from "lucide-react";

import logo from "../assets/images/logo.png";

/** Links configuration */
const links = [
  { id: "home", label: "Home", type: "hash", to: "#home" },
  { id: "about", label: "About", type: "hash", to: "#about" },
  { id: "products", label: "Products", type: "route", to: "/products" },
  { id: "projects", label: "Projects", type: "route", to: "/projects" },
  { id: "contacts", label: "Contacts", type: "hash", to: "#contacts" },
];

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  /** Handle hash scrolling even if not on home */
  const goHash = (hash) => {
    if (location.pathname !== "/") {
      navigate("/", { replace: false });
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
    } else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const navBg = "bg-white border-b border-border-light shadow-sm";
  const navText = "text-text-primary";

  return (
    <nav className={`fixed top-0 w-full z-[60] ${navBg}`}>
      <div className="container mx-auto py-2">
        <div className="relative flex h-16 sm:h-20 items-center justify-between font-sharetech uppercase">
          {/* Brand */}
          <Link to="/" className={`flex items-center ${navText}`}>
            <img
              src={logo}
              alt="Solares logo"
              className="h-24 sm:h-24 w-auto select-none"
              draggable={false}
            />
            <span className="text-lg sm:text-xl text-black font-bold tracking-wide">
              SOLARES
            </span>
          </Link>

          {/* Desktop nav links */}
          <ul
            className={`hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-8 font-medium ${navText}`}
          >
            {links.map((l) => (
              <li key={l.id}>
                {l.type === "route" ? (
                  <Link
                    to={l.to}
                    className={`transition-colors ${scrolled ? "hover:text-green-600" : "hover:text-green-400"
                      }`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => goHash(l.to)}
                    className={`transition-colors ${scrolled ? "hover:text-green-600" : "hover:text-green-400"
                      }`}
                  >
                    {l.label}
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Desktop Phone */}
            <a
              href="tel:+63-XXX-XXX-XXXX"
              className={`hidden lg:inline-flex items-center gap-2 text-sm font-medium text-text-body transition hover:text-accent-sun`}
            >
              <Phone size={16} />
              <span>+63 961 121 2003</span>
            </a>

            {/* Desktop CTA */}
            <button
              onClick={() => goHash("#contacts")}
              className="hidden md:inline-flex px-6 py-2.5 rounded-lg bg-accent-sun hover:bg-orange-600 text-white font-semibold transition transform hover:translate-y-(-1px) hover:shadow-md"
            >
              Get a Free Quote
            </button>

            {/* Burger */}
            <button
              onClick={() => setOpen((v) => !v)}
              className={`md:hidden p-2 rounded transition ${navText} hover:bg-gray-100`}
              aria-label="Toggle menu"
            >
              {open ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.45 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black z-40 md:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              key="drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="fixed right-0 top-0 h-full w-1/2 max-w-[20rem] md:hidden
                         bg-white text-text-primary z-[50] shadow-2xl border-l border-border-light"
            >
              <div className="flex items-center justify-between px-5 h-16 border-b border-border-light">
                <div className="flex items-center gap-2">
                  <img
                    src={logo}
                    alt="Solares logo"
                    className="h-6 w-auto"
                    draggable={false}
                  />
                  <span className="uppercase text-sm tracking-widest text-text-body">
                    Menu
                  </span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="p-2 rounded hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <FiX size={22} />
                </button>
              </div>

              <nav className="py-2">
                <ul className="flex flex-col">
                  {links.map((l) => (
                    <li key={l.id}>
                      {l.type === "route" ? (
                        <Link
                          to={l.to}
                          onClick={() => setOpen(false)}
                          className="block px-5 py-4 text-lg tracking-wide text-text-body hover:bg-accent-sun/10 hover:text-accent-sun transition-colors"
                        >
                          {l.label}
                        </Link>
                      ) : (
                        <button
                          onClick={() => {
                            setOpen(false);
                            goHash(l.to);
                          }}
                          className="block w-full text-left px-5 py-4 text-lg tracking-wide text-text-body hover:bg-accent-sun/10 hover:text-accent-sun transition-colors"
                        >
                          {l.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Mobile CTA */}
                <div className="border-t border-border-light px-5 pt-4 space-y-3">
                  <a
                    href="tel:+63-XXX-XXX-XXXX"
                    className="flex items-center gap-2 w-full rounded-lg bg-border-light/50 text-text-body text-center font-semibold px-4 py-3 hover:bg-border-light transition"
                  >
                    <Phone size={18} />
                    <span>Call us</span>
                  </a>
                  <button
                    onClick={() => {
                      setOpen(false);
                      goHash("#contacts");
                    }}
                    className="block w-full rounded-lg bg-accent-sun hover:bg-orange-600 text-white text-center font-semibold px-4 py-3 transition"
                  >
                    Get a Free Quote
                  </button>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;
